import { Controller } from "stimulus"
import { formatCurrencyAmount } from "../utils/format_currency_amount"
import { formatWholeNumber, parseAmount, parseFormattedNumber, wholeUnits } from "../utils/format_number"
import { calculateOftwImpact, formatDeathsAverted, formatUnits } from "../utils/oftw_impact_calculator"
import { chargeTodayCents, monthsAhead, pledgeStartDate, pledgeStartError } from "../utils/pledge_start"

// OFTW-specific Donation Flow UI: the percentage-of-income pledge calculator (the "pledge" step
// donation_flow_shared_controller.js has no concept of). Everything else -- currency/amount
// handling, address requirements, Gift Aid, the core review summary -- is the shared controller;
// see sharedController below. The one-time page (variant "one_time") has a plain amount step
// instead of the pledge step, and the same portfolio, mix, sidebar and review.
export default class extends Controller {
  static values = { variant: String }

  static targets = [
    "estimatedIncomeDollars", "estimatedIncomeCents", "presetPercentage", "pledgePercentage",
    "incomePresetGroup", "incomePreset", "minimumNote", "pledgeError",
    "customPercentageField", "customPercentage", "customAmountField", "customAmount", "customAmountSymbol",
    "customPledgeNote", "incomePercentageFields", "sidebarPercentageRow",
    "portfolioGroup", "allocateStep", "allocateError", "sidebarTopPicks", "sidebarSplit", "sidebarCard", "sidebarAmount", "sidebarPortfolio", "sidebarPercentage",
    "sidebarImpact", "impactAnnual", "impactDeaths", "impactRows", "impactFootnote",
    // When the pledge starts, and the optional meantime donation until then (see _pledge.html.slim)
    "startOption", "startDateFields", "startMonth", "startYear", "startNote",
    "meantimeSection", "meantimeToggle", "meantimeOptions", "meantimePreset", "meantimeSymbol",
    "meantimeDollars", "meantimeCents", "meantimeNote",
    "sidebarStart", "sidebarMeantimeRow", "sidebarMeantime", "sidebarReminderNotice",
    "summaryTotalLabel", "summaryStartRow", "summaryStart", "summaryPledgeRow", "summaryPledge"
  ]

  initialize() {
    this.element.addEventListener("donation-wizard:pageShown", this.handlePageShown.bind(this))
    this.element.addEventListener("donation-wizard:validate", this.handleWizardValidate.bind(this))
    // hli-tip refreshes the shared summary first, then dispatches this -- re-apply the future-start
    // rows on top of it.
    this.element.addEventListener("hli-tip:changed", () => {
      this.updateReviewStart()
      this.updateReviewPortfolio()
    })
    // Fires on every slider tick -- keep the split it carries rather than recomputing it per read.
    this.element.addEventListener("charity-split:changed", (event) => {
      this.latestCustomMixSplits = event.detail.splits
      this.syncCustomMix()
      if (this.hasAllocateErrorTarget && this.customMixEins.length > 0) this.allocateErrorTarget.classList.add("is-hidden")
      this.updateSidebar()
    })
    // The one-time amount step's input and presets (donation-flow-shared) -- the pledge step
    // updates the sidebar itself.
    this.element.addEventListener("donation-flow-shared:amountChanged", () => this.updateSidebar())
    this.filterPortfoliosByCurrency()
    this.filterIncomePresetsByCurrency()
    this.updateSidebar()
    this.updatePercentagePreviews()
    this.updateMinimumNote()
  }

  get oneTime() {
    return this.variantValue === "one_time"
  }

  get sharedController() {
    return this.application.getControllerForElementAndIdentifier(this.element, "donation-flow-shared")
  }

  // "Add a tip to support One for the World" card, on the Review step -- connects on page load,
  // while the pledge amount is still $0 (income/percentage aren't entered until the pledge step
  // after it), so its default 10% pill computes to $0 and stays there until something re-renders
  // it. Nothing else does that here, unlike happier_lives_institute_donation_controller.js's own
  // amount step, so handlePageShown must refresh it on arrival at Review (see below).
  get hliTipController() {
    const element = this.element.querySelector('[data-controller~="hli-tip"]')
    return element ? this.application.getControllerForElementAndIdentifier(element, "hli-tip") : null
  }

  // Each currency maps to its own regional partner's portfolios (see _portfolio.html.slim) --
  // shows only the selected currency's group and clears any portfolio pick from a group that's
  // about to be hidden, so a stale selection from a different currency can never be submitted.
  // With nothing picked in the shown group, its most popular portfolio (data-recommended) is
  // selected, so switching currency lands on that currency's default rather than on no pick.
  filterPortfoliosByCurrency() {
    if (!this.hasPortfolioGroupTarget) return

    const shared = this.sharedController
    const currency = shared?.hasCurrencySelectTarget ? shared.currencySelectTarget.value : null

    let selectionHidden = false
    let shownGroup = null
    this.portfolioGroupTargets.forEach((group) => {
      const matches = group.dataset.currency === currency
      group.classList.toggle("is-hidden", !matches)
      if (matches) shownGroup = group
      if (!matches && group.querySelector(".is-active")) selectionHidden = true
    })

    this.filterCustomMixByCurrency(currency)

    // On load nothing is marked yet (radio-select connects after this controller): keep the card the
    // field names when it's in the shown group -- a deep link, including one to a single Top Pick,
    // which CheckoutViewModel#requested_portfolio_selection points at the "Choose your own mix" card.
    if (!selectionHidden && shownGroup && !shownGroup.querySelector(".is-active")) {
      const output = shownGroup.closest?.('[data-controller~="radio-select"]')?.querySelector?.('[data-radio-select-target="output"]')
      const named = output?.value && shownGroup.querySelector(`[data-radio-select-value="${CSS.escape(output.value)}"]`)
      named?.classList.add("is-active")
    }

    const recommendedCard = shownGroup?.querySelector("[data-recommended]")
    if (!selectionHidden && (shownGroup?.querySelector(".is-active") || !recommendedCard)) {
      this.syncCustomMix()
      this.updateSidebar()
      return
    }

    const radioSelectElement = this.portfolioGroupTarget.closest('[data-controller~="radio-select"]')
    const radioSelect = radioSelectElement
      ? this.application.getControllerForElementAndIdentifier(radioSelectElement, "radio-select")
      : null
    radioSelect?.clearSelection()
    if (radioSelect?.hasOutputTarget) {
      radioSelect.outputTarget.value = recommendedCard?.dataset.radioSelectValue || ""
      recommendedCard?.classList.add("is-active")
    }
    this.syncCustomMix()
    this.updateSidebar()
  }

  // -- "Choose your own mix" (Flows::ClientConfig.custom_mix_portfolio_name) ----------------------

  get selectedPortfolioCard() {
    return this.element.querySelector('fieldset[data-page-id="portfolio"] [data-radio-select-value].is-active')
  }

  get customMixSelected() {
    return Boolean(this.selectedPortfolioCard?.dataset?.customMix)
  }

  get portfolioField() {
    return this.element.querySelector('[name="donation_form[managed_portfolio_id]"]')
  }

  get radioSelectController() {
    const element = this.portfolioField?.closest('[data-controller~="radio-select"]')
    return element ? this.application.getControllerForElementAndIdentifier(element, "radio-select") : null
  }

  get charitySplitController() {
    return this.hasAllocateStepTarget
      ? this.application.getControllerForElementAndIdentifier(this.element, "charity-split")
      : null
  }

  // { ein: percentage } of the donor's mix, leaving out a charity slid to 0% (the server drops it
  // too) -- {} with nothing added.
  get customMixSplits() {
    const splits = this.latestCustomMixSplits ?? this.charitySplitController?.roundedSplits() ?? {}
    return Object.fromEntries(Object.entries(splits).filter(([, percentage]) => percentage > 0))
  }

  get customMixEins() {
    return Object.keys(this.customMixSplits)
  }

  choosePortfolio() {
    this.syncCustomMix()
    this.updateSidebar()
    this.refreshWizardSteps()
  }

  // Picking or leaving the custom mix adds or drops the Allocate step from the progress bar.
  refreshWizardSteps() {
    const wizard = this.application.getControllerForElementAndIdentifier(this.element, "donation-wizard")
    wizard?.renderProgress()
    wizard?.renderStepEyebrows()
  }

  // The custom mix submits its portfolio (Top Picks) plus the Allocate step's split as
  // custom_allocations; Flows::Bricks::SelectPortfolio decides what that's saved as (the donor's own
  // portfolio, or Top Picks / a single Top Pick's when it's one of those -- Flows::CustomMix). The
  // Allocate step is disabled, so nothing in it is submitted, for any other pick.
  syncCustomMix() {
    if (!this.hasAllocateStepTarget) return

    const card = this.selectedPortfolioCard
    const customMix = Boolean(card?.dataset?.customMix)
    this.allocateStepTarget.disabled = !customMix
    // Before radio-select connects (a deep link opening on this card), the field keeps naming the
    // card so radio-select marks it; charity-split:changed syncs once the linked charity is added.
    if (customMix && this.portfolioField && this.radioSelectController) {
      this.portfolioField.value = card.dataset.customMixPortfolioId
    }
  }

  // Only the selected currency's charities: its mix portfolio's (data-custom-mix-currencies), on the
  // Allocate step and in the sidebar's Top Picks box. One added under another currency is removed.
  filterCustomMixByCurrency(currency) {
    if (!this.hasAllocateStepTarget && !this.hasSidebarTopPicksTarget) return

    const containers = [this.hasAllocateStepTarget && this.allocateStepTarget, this.hasSidebarTopPicksTarget && this.sidebarTopPicksTarget]
    containers.filter(Boolean).forEach((container) => {
      container.querySelectorAll("[data-custom-mix-currencies]").forEach((el) => {
        el.classList.toggle("is-hidden", !el.dataset.customMixCurrencies.split(" ").includes(currency))
      })
    })

    const split = this.charitySplitController
    if (!split || !this.hasAllocateStepTarget) return
    split.addedEins.slice().forEach((ein) => {
      const card = this.allocateStepTarget.querySelector(`[data-charity-split-target="pickerCard"][data-charity-ein="${CSS.escape(ein)}"]`)
      if (card?.classList.contains("is-hidden")) split.removeCharity(ein)
    })
  }

  // "AMF 60%, Malaria Consortium 40%", or the one charity's full name.
  get customMixLabel() {
    const eins = this.customMixEins
    if (eins.length === 0) return "Your own mix"
    const charities = eins.map((ein) => this.charitySplitController?.charityFor(ein))
    if (eins.length === 1) return charities[0]?.name || ""

    const splits = this.customMixSplits
    return eins.map((ein, i) => `${charities[i]?.short_name || charities[i]?.name} ${splits[ein]}%`).join(", ")
  }

  // The shared review summary names the picked card -- for the custom mix, say what's in it.
  updateReviewPortfolio() {
    const shared = this.sharedController
    if (!this.customMixSelected || !shared?.hasSummaryPortfolioTarget) return

    shared.summaryPortfolioTarget.textContent = this.customMixLabel
  }

  handleWizardValidate(event) {
    const shared = this.sharedController
    if (!shared) return

    if (event.detail.pageId === "pledge") {
      const meetsMinimum = shared.validateAmount()
      this.flagMinimumPledge(!meetsMinimum)
      if (!meetsMinimum) event.preventDefault()
      if (!this.validateCustomPledge()) event.preventDefault()
      if (!this.validatePledgeStart()) event.preventDefault()
    }
    if (event.detail.pageId === "amount" && !shared.validateAmount()) event.preventDefault()
    if (event.detail.pageId === "personal_info" && !shared.validatePostcode()) event.preventDefault()
    if (event.detail.pageId === "allocate" && this.customMixEins.length === 0) {
      if (this.hasAllocateErrorTarget) this.allocateErrorTarget.classList.remove("is-hidden")
      event.preventDefault()
    }
  }

  handlePageShown(event) {
    if (event.detail.pageId === "pledge") {
      // The currency (and so the minimum's symbol) is picked on the step before -- and can change
      // after the donor goes back, so every symbol on this step is refreshed on each arrival.
      this.updateMinimumNote()
      if (this.hasCustomAmountSymbolTarget) this.customAmountSymbolTarget.textContent = this.currencySymbol
      this.meantimeSymbolTargets.forEach((el) => { el.textContent = this.currencySymbol })
      // Re-derived in the (possibly new) currency's minor units.
      if (this.hasMeantimeDollarsTarget && this.meantimeDollarsTarget.value) this.updateMeantimeCents()
      this.applyDefaultIncome()
    }
    // Re-derived in the currency picked on the step before, which can change after going back.
    if (event.detail.pageId === "amount") {
      this.amountStepShown = true
      this.sharedController?.updateAmountCents()
    }
    if (event.detail.pageId === "review") {
      // Refresh the tip card first -- its recomputed amount (from the now-known pledge amount)
      // feeds the summary's Tip row/Total charge, and the hidden tip_cents field the PaymentIntent
      // actually charges (see donation_flow_payment_controller.js).
      this.hliTipController?.refresh()
      this.sharedController?.updateReviewSummaryCore()
      this.updateReviewStart()
      this.updateReviewPortfolio()
    }
    this.updateSidebar()
  }

  // -- Suggested incomes (Flows::PledgeIncomePresets) -------------------------------------------

  get visibleIncomePresetGroup() {
    return this.incomePresetGroupTargets.find((group) => !group.classList.contains("is-hidden"))
  }

  // Only the selected currency's suggestions are shown. An income that came from another
  // currency's suggestion is cleared rather than reread in the new currency (e.g. $100k as
  // £100k), so the new currency's default applies on reaching the pledge step instead.
  filterIncomePresetsByCurrency() {
    if (!this.hasIncomePresetGroupTarget) return

    const shared = this.sharedController
    const currency = shared?.hasCurrencySelectTarget ? shared.currencySelectTarget.value : null
    this.incomePresetGroupTargets.forEach((group) => group.classList.toggle("is-hidden", group.dataset.currency !== currency))

    // Only a suggested income is cleared ($100k mustn't become £100k); one the donor typed is kept
    // as their own number in the new currency.
    if (this.incomeFromPreset && this.hasEstimatedIncomeDollarsTarget) {
      this.estimatedIncomeDollarsTarget.value = ""
      this.incomeFromPreset = false
      this.incomeTouched = false
      this.updatePledgeEstimate()
    }
    this.markActiveIncomePreset()
  }

  // Pre-fills the currency's default suggestion the first time the donor reaches the pledge step
  // -- not on load, so the sidebar stays hidden on the portfolio step (see _sidebar.html.slim).
  applyDefaultIncome() {
    const group = this.visibleIncomePresetGroup
    if (!group || this.incomeTouched || !this.hasEstimatedIncomeDollarsTarget || this.estimatedIncomeDollarsTarget.value) return

    this.setIncome(group.dataset.defaultIncome)
  }

  selectIncome(event) {
    this.incomeTouched = true
    this.setIncome(event.currentTarget.dataset.income)
  }

  setIncome(income) {
    this.estimatedIncomeDollarsTarget.value = formatWholeNumber(income)
    this.estimatedIncomeDollarsTarget.classList.remove("is-danger")
    this.incomeFromPreset = true
    this.markActiveIncomePreset()
    this.updatePledgeEstimate()
  }

  incomeTyped() {
    this.incomeTouched = true
    this.incomeFromPreset = false
    this.estimatedIncomeDollarsTarget.classList.remove("is-danger")
    this.clearCustomPledgeError()
    this.markActiveIncomePreset()
    this.updatePledgeEstimate()
  }

  // Commas in the field after typing, without fighting the caret on each keystroke.
  // Truncating (65000.99 → 65,000) must re-run the estimate, or hidden cents still reflect the decimal.
  // Non-numeric input (NaN) is left as typed — don't replace "abc" with a blank field.
  incomeBlurred() {
    if (!this.hasEstimatedIncomeDollarsTarget) return
    const income = this.incomeDollars
    if (!Number.isFinite(income)) return
    this.estimatedIncomeDollarsTarget.value = formatWholeNumber(income)
    this.updatePledgeEstimate()
  }

  get incomeDollars() {
    return this.hasEstimatedIncomeDollarsTarget ? wholeUnits(this.estimatedIncomeDollarsTarget.value) : NaN
  }

  // Highlights the suggestion matching the income field, typed or clicked.
  markActiveIncomePreset() {
    const income = this.incomeDollars
    const group = this.visibleIncomePresetGroup
    this.incomePresetTargets.forEach((btn) => {
      btn.classList.toggle("is-active", btn.parentElement === group && parseFormattedNumber(btn.dataset.income) === income)
    })
  }

  // -- Minimum pledge (checked by donation-flow-shared#validateAmount) ---------------------------

  get minimumCents() {
    const shared = this.sharedController
    return Math.round((shared?.minimumContributionAmountValue || 0) * (shared?.centsMultiplier || 100))
  }

  get pledgeCents() {
    const shared = this.sharedController
    const dollars = shared?.hasAmountDollarsTarget ? parseAmount(shared.amountDollarsTarget.value) : NaN
    return isNaN(dollars) ? 0 : Math.round(dollars * shared.centsMultiplier)
  }

  updateMinimumNote() {
    if (!this.hasMinimumNoteTarget) return
    this.minimumNoteTarget.textContent = `The minimum pledge is ${this.formatCopyCents(this.minimumCents)}/month.`
  }

  // Continue blocked by a pledge under the minimum: the minimum note and the chosen percentage turn
  // red, and the top of the step says why. Re-checked on every pledge change (refreshMinimumPledge)
  // so it clears as soon as the pledge reaches the minimum.
  flagMinimumPledge(flagged) {
    this.minimumFlagged = flagged
    if (this.hasMinimumNoteTarget) this.minimumNoteTarget.classList.toggle("is-error", flagged)
    this.presetPercentageTargets.forEach((btn) => btn.classList.toggle("is-danger", flagged && btn.classList.contains("is-active")))
    this.activeCustomControl?.classList.toggle("is-danger", flagged)
    if (!this.hasPledgeErrorTarget) return

    this.pledgeErrorTarget.classList.toggle("is-hidden", !flagged)
    this.pledgeErrorTarget.textContent = flagged ? this.minimumPledgeMessage : ""
  }

  get minimumPledgeMessage() {
    const minimum = `${this.formatCopyCents(this.minimumCents)}/month`
    const pledge = this.pledgeCents
    if (!pledge && this.pledgeMode === "amount") return `Enter a monthly amount. The minimum pledge is ${minimum}.`
    if (!pledge && !this.estimatedIncomeDollarsTarget.value) return `Enter your estimated annual income to calculate your pledge. The minimum pledge is ${minimum}.`
    if (!pledge) return `Enter a percentage or monthly amount to calculate your pledge. The minimum pledge is ${minimum}.`
    // A donor who typed a monthly amount gets told to raise that, not to pick a percentage.
    const fix = this.pledgeMode === "amount" ? "Enter a higher monthly amount." : "Choose a higher percentage, or check your income."
    return `Your pledge of ${this.formatCopyCents(pledge)}/month is below the ${minimum} minimum. ${fix}`
  }

  refreshMinimumPledge() {
    this.updateMinimumNote()
    if (this.minimumFlagged) this.flagMinimumPledge(this.pledgeCents < this.minimumCents)
  }

  // -- Custom pledge: any percentage, or a monthly amount instead (see _pledge.html.slim) ---------

  showCustomPercentage() {
    this.setPledgeMode("percentage")
    this.customPercentageTyped()
  }

  showCustomAmount() {
    this.incomePledgeMode = this.pledgeMode || "preset"
    this.incomePledgePercentage = this.pledgePercentageTarget.value
    this.setPledgeMode("amount")
    this.updatePledgeEstimate()
  }

  showIncomePercentage() {
    const mode = this.incomePledgeMode || "preset"
    const percentage = this.incomePledgePercentage || "1"
    this.setPledgeMode(mode)
    this.pledgePercentageTarget.value = percentage
    if (mode === "percentage") this.customPercentageTarget.value = percentage
    this.presetPercentageTargets.forEach((btn) => btn.classList.toggle("is-active", mode === "preset" && btn.dataset.percentage === percentage))
    this.applyDefaultIncome()
    this.updatePledgeEstimate()
    if (mode !== "percentage") this.estimatedIncomeDollarsTarget.focus()
  }

  // "preset" (the percentage cards), "percentage" (a typed percentage) or "amount" (a typed monthly
  // amount). Switching clears the other inputs, so exactly one thing drives the pledge.
  setPledgeMode(mode) {
    this.pledgeMode = mode
    if (this.hasIncomePercentageFieldsTarget) {
      this.incomePercentageFieldsTarget.classList.toggle("is-hidden", mode === "amount")
      this.incomePercentageFieldsTarget.querySelectorAll("input").forEach((input) => { input.disabled = mode === "amount" })
    }
    if (this.hasCustomPercentageFieldTarget) this.customPercentageFieldTarget.classList.toggle("is-hidden", mode !== "percentage")
    if (this.hasCustomAmountFieldTarget) this.customAmountFieldTarget.classList.toggle("is-hidden", mode !== "amount")
    if (mode !== "percentage" && this.hasCustomPercentageTarget) this.customPercentageTarget.value = ""
    if (mode !== "amount" && this.hasCustomAmountTarget) this.customAmountTarget.value = ""
    if (this.hasCustomPledgeNoteTarget) this.customPledgeNoteTarget.classList.add("is-hidden")
    this.element.querySelectorAll(".donation-flow__custom-pledge .donation-flow__control").forEach((control) => control.classList.remove("is-danger"))
    if (mode === "preset") return

    this.presetPercentageTargets.forEach((btn) => btn.classList.remove("is-active", "is-danger"))
    if (this.hasCustomAmountSymbolTarget) this.customAmountSymbolTarget.textContent = this.currencySymbol
    const input = mode === "percentage" ? this.customPercentageTarget : this.customAmountTarget
    input.focus()
  }

  get activeCustomControl() {
    if (this.pledgeMode === "percentage" && this.hasCustomPercentageTarget) return this.customPercentageTarget.closest(".donation-flow__control")
    if (this.pledgeMode === "amount" && this.hasCustomAmountTarget) return this.customAmountTarget.closest(".donation-flow__control")
    return null
  }

  customPercentageTyped() {
    this.clearCustomPledgeError()
    // Rounded to subscriptions.pledge_percentage's scale, as CapturePledgeCommitment does before
    // its income x percentage check -- so what's billed is what's saved.
    const percentage = Math.round(parseFloat(this.customPercentageTarget.value) * 10000) / 10000
    this.pledgePercentageTarget.value = isNaN(percentage) ? "" : String(percentage)
    this.updatePledgeEstimate()
  }

  customAmountTyped() {
    this.clearCustomPledgeError()
    this.updatePledgeEstimate()
  }

  // Typing clears validateCustomPledge's error -- re-checked on the next Continue. (A minimum-pledge
  // flag on the same input is re-applied by refreshMinimumPledge if it still holds.)
  clearCustomPledgeError() {
    if (this.hasCustomPledgeNoteTarget) this.customPledgeNoteTarget.classList.add("is-hidden")
    this.activeCustomControl?.classList.remove("is-danger")
  }

  formatPercentage(percentage) {
    return parseFloat(percentage).toLocaleString("en-US", { maximumFractionDigits: 2 })
  }

  // Custom inputs must be positive; only percentage mode is capped at 100% of income.
  validateCustomPledge() {
    if (this.pledgeMode !== "percentage" && this.pledgeMode !== "amount") return true

    const percentage = parseFloat(this.pledgePercentageTarget.value)
    let error = null
    if (this.pledgeMode === "percentage" && !(percentage > 0)) {
      error = "Enter the percentage of your income you'd like to pledge."
    } else if (this.pledgeMode === "percentage" && percentage > 100) {
      error = "Your pledge can be at most 100% of your income."
    } else if (this.pledgeMode === "amount" && !(parseFormattedNumber(this.customAmountTarget.value) > 0)) {
      error = "Enter the monthly amount you'd like to pledge."
    }

    if (this.hasCustomPledgeNoteTarget) {
      this.customPledgeNoteTarget.textContent = error || ""
      this.customPledgeNoteTarget.classList.toggle("is-hidden", !error)
    }
    if (error) this.activeCustomControl?.classList.add("is-danger")
    return !error
  }

  selectPercentage(event) {
    if (!this.hasPledgePercentageTarget) return

    this.setPledgeMode("preset")
    this.pledgePercentageTarget.value = event.currentTarget.dataset.percentage
    this.presetPercentageTargets.forEach((btn) => btn.classList.toggle("is-active", btn === event.currentTarget))
    this.updatePledgeEstimate()
  }

  // Persistent "Your pledge" preview alongside the form (see
  // donation_flows/sidebars/one-for-the-world/_sidebar.html.slim) -- kept in sync from every place
  // that can change what it shows: page navigation, portfolio pick, percentage/income entry.
  updateSidebar() {
    const shared = this.sharedController

    const dollars = shared?.hasAmountDollarsTarget ? parseAmount(shared.amountDollarsTarget.value) : NaN
    const hasAmount = !isNaN(dollars) && dollars > 0

    // Only the split controls follow the Allocate step; the pledge/impact/reminder cards remain
    // alongside them when going back to edit a mix after entering a pledge amount.
    if (this.hasSidebarSplitTarget) {
      const onAllocateStep = this.hasAllocateStepTarget && !this.allocateStepTarget.classList.contains("is-hidden")
      this.sidebarSplitTarget.classList.toggle("is-hidden", !onAllocateStep)
    }

    // Nothing to preview until the pledge step produces an amount -- see _sidebar.html.slim. The
    // one-time amount starts filled in, so it waits for the amount step instead: otherwise the
    // default would show from the first step as if the donor had already chosen it.
    const amountChosen = hasAmount && (!this.oneTime || this.amountStepShown)
    if (this.hasSidebarCardTarget) this.sidebarCardTarget.classList.toggle("is-hidden", !amountChosen)

    if (this.hasSidebarAmountTarget) {
      if (hasAmount) {
        const cents = Math.round(dollars * shared.centsMultiplier)
        this.sidebarAmountTarget.textContent = this.oneTime ? this.formatCopyCents(cents) : `${this.formatCopyCents(cents)}/month`
      } else {
        this.sidebarAmountTarget.textContent = "—"
      }
    }

    // Only on the step where the portfolio is picked -- later steps have the pledge card instead.
    if (this.hasSidebarTopPicksTarget) {
      const card = this.selectedPortfolioCard
      const onPortfolioStep = !this.element.querySelector('fieldset[data-page-id="portfolio"]')?.classList.contains("is-hidden")
      this.sidebarTopPicksTarget.classList.toggle("is-hidden", !onPortfolioStep || !(card?.dataset?.customMix || card?.dataset?.customMixSource))
    }

    if (this.hasSidebarPortfolioTarget) {
      const selectedCard = this.selectedPortfolioCard
      this.sidebarPortfolioTarget.textContent = this.customMixSelected
        ? this.customMixLabel
        : selectedCard?.querySelector(".donation-flow__option-card-title")?.textContent || "—"
    }

    if (this.hasSidebarPercentageRowTarget) this.sidebarPercentageRowTarget.classList.toggle("is-hidden", this.pledgeMode === "amount")
    if (this.hasSidebarPercentageTarget) {
      const income = this.incomeDollars
      if (this.hasPledgePercentageTarget && this.pledgePercentageTarget.value && Number.isFinite(income)) {
        const symbol = shared?.hasAmountCurrencySymbolTarget ? shared.amountCurrencySymbolTarget.textContent : "$"
        this.sidebarPercentageTarget.textContent = `${this.formatPercentage(this.pledgePercentageTarget.value)}% of ${symbol}${formatWholeNumber(income)}`
      } else {
        this.sidebarPercentageTarget.textContent = "—"
      }
    }

    if (this.hasSidebarStartTarget) {
      const start = this.startDate
      if (start) {
        this.sidebarStartTarget.textContent = start.toLocaleDateString("en-US", { month: "long", year: "numeric" })
      } else {
        // "In a future month" picked but no valid month/year yet -- still not "Now".
        this.sidebarStartTarget.textContent = this.startsInFuture ? "A future month" : "Now"
      }
    }
    const meantimeCents = this.meantimeCents
    if (this.hasSidebarMeantimeRowTarget) this.sidebarMeantimeRowTarget.classList.toggle("is-hidden", !meantimeCents)
    if (this.hasSidebarMeantimeTarget && meantimeCents) {
      this.sidebarMeantimeTarget.textContent = `${this.formatCopyCents(meantimeCents)}/month`
    }
    // "We'll notify you before your first pledge donation" is only true of a future start.
    if (this.hasSidebarReminderNoticeTarget) this.sidebarReminderNoticeTarget.classList.toggle("is-hidden", !this.startDate)

    this.updateImpactPreview()
  }

  // -- When the pledge starts (Flows::PledgeStart, mirrored in utils/pledge_start.js) -------------

  get today() {
    const iso = this.hasStartDateFieldsTarget ? this.startDateFieldsTarget.dataset.today : null
    if (!iso) return new Date()
    const [year, month, day] = iso.split("-").map(Number)
    return new Date(year, month - 1, day)
  }

  get startsInFuture() {
    return this.startOptionTargets.some((btn) => btn.dataset.start === "future" && btn.classList.contains("is-active"))
  }

  get chosenStart() {
    if (!this.startsInFuture || !this.hasStartMonthTarget || !this.hasStartYearTarget) return {}
    return { month: parseInt(this.startMonthTarget.value, 10) || null, year: parseInt(this.startYearTarget.value, 10) || null }
  }

  // The pledge's start date, or null for "Now" (or a future month not yet validly chosen).
  get startDate() {
    const { month, year } = this.chosenStart
    if (!month || !year) return null
    if (pledgeStartError({ month, year, today: this.today, maxYearsAhead: this.maxYearsAhead })) return null
    return pledgeStartDate({ month, year, today: this.today })
  }

  get maxYearsAhead() {
    return (this.hasStartDateFieldsTarget && parseInt(this.startDateFieldsTarget.dataset.maxYearsAhead, 10)) || 5
  }

  get meantimeCents() {
    return this.hasMeantimeCentsTarget ? (parseInt(this.meantimeCentsTarget.value, 10) || 0) : 0
  }

  get currencySymbol() {
    const shared = this.sharedController
    return shared?.hasAmountCurrencySymbolTarget ? shared.amountCurrencySymbolTarget.textContent : "$"
  }

  formatCents(cents, { stripWholeCents = false } = {}) {
    return formatCurrencyAmount(cents, this.currencySymbol, this.sharedController?.isZeroDecimalCurrency, { stripWholeCents })
  }

  formatCopyCents(cents) {
    return this.formatCents(cents, { stripWholeCents: true })
  }

  selectStart(event) {
    this.startOptionTargets.forEach((btn) => btn.classList.toggle("is-active", btn === event.currentTarget))
    const future = event.currentTarget.dataset.start === "future"
    if (this.hasStartDateFieldsTarget) this.startDateFieldsTarget.classList.toggle("is-hidden", !future)
    // Blank month/year is what "Now" submits as -- see Flows::PledgeStart.
    if (!future && this.hasStartMonthTarget) this.startMonthTarget.value = ""
    if (!future && this.hasStartYearTarget) this.startYearTarget.value = ""
    this.startChanged()
  }

  startChanged() {
    if (this.hasStartNoteTarget) this.startNoteTarget.classList.add("is-hidden")
    this.restrictStartOptions()
    const start = this.startDate
    const { month, year } = this.chosenStart
    const minMonths = (this.hasMeantimeSectionTarget && parseInt(this.meantimeSectionTarget.dataset.minMonthsAhead, 10)) || 2
    const meantimeOffered = Boolean(start) && monthsAhead({ month, year, today: this.today }) >= minMonths

    if (this.hasMeantimeSectionTarget) this.meantimeSectionTarget.classList.toggle("is-hidden", !meantimeOffered)
    // A meantime donation chosen for a later month the donor then moved away from isn't carried
    // over -- the server would drop it anyway (Flows::PledgeStart#trial_amount_cents).
    if (!meantimeOffered) this.clearMeantime()
    this.meantimeSymbolTargets.forEach((el) => { el.textContent = this.currencySymbol })
    this.updateSidebar()
  }

  // Disables each month/year option that can't make a valid start with the other select's current
  // choice -- this month or earlier (e.g. October once this year is picked), or past the furthest
  // allowed -- so an invalid start can't be picked at all, rather than only caught on Continue.
  restrictStartOptions() {
    if (!this.hasStartMonthTarget || !this.hasStartYearTarget) return

    const { today, maxYearsAhead } = this
    const valid = (month, year) => !pledgeStartError({ month, year, today, maxYearsAhead })
    const month = parseInt(this.startMonthTarget.value, 10) || null
    const year = parseInt(this.startYearTarget.value, 10) || null

    Array.from(this.startMonthTarget.options).forEach((option) => {
      const optionMonth = parseInt(option.value, 10)
      option.disabled = Boolean(optionMonth && year) && !valid(optionMonth, year)
    })
    Array.from(this.startYearTarget.options).forEach((option) => {
      const optionYear = parseInt(option.value, 10)
      option.disabled = Boolean(optionYear && month) && !valid(month, optionYear)
    })
  }

  toggleMeantime() {
    const on = this.hasMeantimeToggleTarget && this.meantimeToggleTarget.checked
    if (this.hasMeantimeOptionsTarget) this.meantimeOptionsTarget.classList.toggle("is-hidden", !on)
    if (!on) this.clearMeantime({ keepToggle: true })
    this.updateSidebar()
  }

  selectMeantime(event) {
    this.meantimePresetTargets.forEach((btn) => btn.classList.toggle("is-active", btn === event.currentTarget))
    if (this.hasMeantimeDollarsTarget) this.meantimeDollarsTarget.value = event.currentTarget.dataset.amount
    this.updateMeantimeCents()
  }

  meantimeTyped() {
    this.meantimePresetTargets.forEach((btn) => btn.classList.remove("is-active"))
    this.updateMeantimeCents()
  }

  updateMeantimeCents() {
    if (this.hasMeantimeNoteTarget) this.meantimeNoteTarget.classList.add("is-hidden")
    const dollars = this.hasMeantimeDollarsTarget ? parseFormattedNumber(this.meantimeDollarsTarget.value) : NaN
    const multiplier = this.sharedController?.centsMultiplier || 100
    if (this.hasMeantimeCentsTarget) this.meantimeCentsTarget.value = isNaN(dollars) ? "" : Math.round(dollars * multiplier)
    this.updateSidebar()
  }

  clearMeantime({ keepToggle = false } = {}) {
    if (!keepToggle && this.hasMeantimeToggleTarget) this.meantimeToggleTarget.checked = false
    if (!keepToggle && this.hasMeantimeOptionsTarget) this.meantimeOptionsTarget.classList.add("is-hidden")
    this.meantimePresetTargets.forEach((btn) => btn.classList.remove("is-active"))
    if (this.hasMeantimeDollarsTarget) this.meantimeDollarsTarget.value = ""
    if (this.hasMeantimeCentsTarget) this.meantimeCentsTarget.value = ""
  }

  // Pledge step's own checks, on top of the shared amount check -- the server enforces the same
  // (Flows::PledgeStart), this just keeps the donor on the step with a message instead.
  validatePledgeStart() {
    if (!this.startsInFuture) return true

    const { month, year } = this.chosenStart
    const error = pledgeStartError({ month, year, today: this.today, maxYearsAhead: this.maxYearsAhead }) ||
      (!month && !year ? "Choose the month and year your pledge starts." : null)
    if (error) {
      if (this.hasStartNoteTarget) {
        this.startNoteTarget.textContent = error
        this.startNoteTarget.classList.remove("is-hidden")
      }
      return false
    }

    const meantimeOn = this.hasMeantimeToggleTarget && this.meantimeToggleTarget.checked &&
      this.hasMeantimeSectionTarget && !this.meantimeSectionTarget.classList.contains("is-hidden")
    const minCents = (this.hasMeantimeSectionTarget && parseInt(this.meantimeSectionTarget.dataset.minCents, 10)) || 100
    if (meantimeOn && this.meantimeCents < minCents) {
      if (this.hasMeantimeNoteTarget) {
        this.meantimeNoteTarget.textContent = `Choose an amount of at least ${this.formatCopyCents(minCents)}, or untick this to give nothing until your pledge starts.`
        this.meantimeNoteTarget.classList.remove("is-hidden")
      }
      return false
    }
    return true
  }

  // Review summary for a future start: "Total charge" becomes today's charge (the first meantime
  // donation, or nothing), plus when the pledge starts and what it charges from then.
  updateReviewStart() {
    const start = this.startDate
    const shared = this.sharedController
    if (this.hasSummaryStartRowTarget) this.summaryStartRowTarget.classList.toggle("is-hidden", !start)
    if (this.hasSummaryPledgeRowTarget) this.summaryPledgeRowTarget.classList.toggle("is-hidden", !start)
    if (this.hasSummaryTotalLabelTarget) this.summaryTotalLabelTarget.textContent = start ? "Charged today" : "Total charge"
    if (!shared) return

    const amountCents = shared.hasAmountCentsTarget ? (parseInt(shared.amountCentsTarget.value, 10) || 0) : 0
    const tipCents = typeof shared.currentTipCents === "function" ? shared.currentTipCents() : 0
    if (!start) {
      if (shared.hasSummaryAmountTarget) shared.summaryAmountTarget.textContent = this.formatCopyCents(amountCents + tipCents)
      if (shared.hasSummaryTipTarget && tipCents > 0) shared.summaryTipTarget.textContent = this.formatCopyCents(tipCents)
      return
    }

    const today = chargeTodayCents({ future: true, amountCents, tipCents, trialAmountCents: this.meantimeCents })
    if (shared.hasSummaryAmountTarget) shared.summaryAmountTarget.textContent = this.formatCopyCents(today)
    // The tip row would otherwise show the pledge's tip right next to a smaller "Charged today" --
    // say what's tipped today (at the same percentage of the meantime donation) and from the start.
    if (shared.hasSummaryTipTarget && tipCents > 0) {
      const tipToday = this.meantimeCents ? today - this.meantimeCents : 0
      shared.summaryTipTarget.textContent = tipToday > 0
        ? `${this.formatCopyCents(tipToday)} today, then ${this.formatCopyCents(tipCents)}/month`
        : `${this.formatCopyCents(tipCents)}/month from your start date`
    }
    if (this.hasSummaryStartTarget) {
      this.summaryStartTarget.textContent = start.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" })
    }
    if (this.hasSummaryPledgeTarget) this.summaryPledgeTarget.textContent = `${this.formatCopyCents(amountCents + tipCents)}/month`
  }

  // "Your impact each year" (see oftw_impact_calculator.js) -- shown for ANY selected portfolio
  // that config/impact_calculators/one-for-the-world.yml's charities cover at least partially
  // (see CheckoutViewModel#impact_calculator_allocations_by_portfolio for what "cover" means: real
  // allocation percentages, not an assumed equal split); hidden for a portfolio matching none.
  updateImpactPreview() {
    if (!this.hasSidebarImpactTarget) return

    const selectedCard = this.selectedPortfolioCard
    const selectedPortfolioId = selectedCard?.dataset?.customMixPortfolioId || selectedCard?.dataset.radioSelectValue
    const impactByPortfolio = JSON.parse(this.sidebarImpactTarget.dataset.impactByPortfolio)
    let allocations = selectedPortfolioId ? impactByPortfolio[selectedPortfolioId] : null
    // The mix's own split, using the figures of the portfolio it's built from.
    if (allocations && this.customMixSelected) {
      const splits = this.customMixSplits
      allocations = allocations.filter((a) => splits[a.ein]).map((a) => ({ ...a, share_percent: splits[a.ein] }))
      if (allocations.length === 0) allocations = null
    }
    this.sidebarImpactTarget.classList.toggle("is-hidden", !allocations)
    if (!allocations) return

    const shared = this.sharedController
    const incomeDollars = this.incomeDollars
    const percentage = this.hasPledgePercentageTarget ? parseFloat(this.pledgePercentageTarget.value) : NaN
    // Fixed monthly giving has no income commitment; its billed amount still has an impact. A
    // one-time donation's impact is the donation itself, not a year of it.
    let annualDollars
    if (this.oneTime) {
      annualDollars = shared?.hasAmountDollarsTarget ? parseAmount(shared.amountDollarsTarget.value) : NaN
    } else {
      annualDollars = this.pledgeMode === "amount"
        ? this.pledgeCents * 12 / (shared?.centsMultiplier || 100)
        : incomeDollars * (percentage / 100)
    }
    if (!Number.isFinite(annualDollars) || annualDollars <= 0 || !shared) {
      this.sidebarImpactTarget.classList.toggle("is-hidden", true)
      return
    }

    const unitsPerUsd = shared.hasAmountCurrencySymbolTarget
      ? parseFloat(shared.amountCurrencySymbolTarget.dataset.unitsPerUsd) || 1
      : 1
    const multiplier = shared.centsMultiplier
    const isZeroDecimal = shared.isZeroDecimalCurrency

    // Percentage mode uses income x percentage; fixed amounts use monthly giving x 12.
    const annualCents = Math.round(annualDollars * multiplier)

    const { totalDeathsAverted, costPerDeathAverted, matchedSharePercent, breakdown } = calculateOftwImpact({
      amountCents: annualCents, allocations, isZeroDecimal, unitsPerUsd
    })

    if (this.hasImpactAnnualTarget) {
      this.impactAnnualTarget.textContent = `${this.formatCopyCents(annualCents)} donated`
    }
    if (this.hasImpactDeathsTarget) this.impactDeathsTarget.textContent = formatDeathsAverted(totalDeathsAverted)
    if (this.hasImpactRowsTarget) {
      this.impactRowsTarget.innerHTML = breakdown.map((row) => {
        const amountCents = Math.round(row.allocatedLocal * multiplier)
        const costPerOutputCents = Math.round(row.costPerOutput * unitsPerUsd * multiplier)
        return `
          <div class="donation-flow__sidebar-impact-row">
            <img src="${row.logo}" alt="" class="donation-flow__sidebar-impact-logo">
            <div class="donation-flow__sidebar-impact-units">${formatUnits(row.units)}</div>
            <div class="donation-flow__sidebar-impact-row-body">
              <div class="donation-flow__sidebar-impact-outcome">${row.outcomeLabel}</div>
              <div class="donation-flow__sidebar-impact-cost"><strong>${this.formatCopyCents(amountCents)}</strong> to ${row.name} · <strong>${this.formatCopyCents(costPerOutputCents)}</strong> each</div>
            </div>
          </div>
        `
      }).join("")
    }
    if (this.hasImpactFootnoteTarget) {
      const costPerDeathCents = Math.round(costPerDeathAverted * unitsPerUsd * multiplier)
      const costPerDeathLabel = this.formatCopyCents(costPerDeathCents)
      const givewellNote = "Figures follow GiveWell's published cost-effectiveness data, the same numbers the website's impact calculator uses."

      let summary
      if (breakdown.length === 1) {
        summary = `${Math.round(matchedSharePercent)}% goes to ${breakdown[0].name}.`
      } else {
        const remainder = 100 - Math.round(matchedSharePercent)
        const remainderNote = remainder > 0 ? ` (the remaining ${remainder}% has no published cost-effectiveness figures)` : ""
        const basis = this.customMixSelected ? "your mix" : "your portfolio's allocation"
        summary = `Split ${Math.round(matchedSharePercent)}% across ${breakdown.length} GiveWell charities based on ${basis}${remainderNote}.`
      }
      this.impactFootnoteTarget.textContent = `${summary} Blended cost per life saved: ${costPerDeathLabel}. ${givewellNote}`
    }
  }

  // Percentage mode divides income x percentage into monthly giving; fixed-amount mode uses the
  // typed monthly amount directly. Both write to the shared amountDollars/amountCents targets,
  // so the payment and subscription pipeline can consume the same amount in either mode.
  updatePledgeEstimate() {
    const shared = this.sharedController
    if (!shared || !this.hasEstimatedIncomeDollarsTarget || !this.hasPledgePercentageTarget) return

    const incomeDollars = this.incomeDollars
    const percentage = parseFloat(this.pledgePercentageTarget.value)
    const multiplier = shared.centsMultiplier
    const incomeCents = isNaN(incomeDollars) ? NaN : Math.round(incomeDollars * multiplier)

    if (this.hasEstimatedIncomeCentsTarget) {
      this.estimatedIncomeCentsTarget.value = isNaN(incomeCents) ? "" : incomeCents
    }

    if (this.pledgeMode === "amount" && shared.hasAmountDollarsTarget) {
      this.applyCustomAmount()
      return
    }

    if (isNaN(incomeCents) || isNaN(percentage) || !shared.hasAmountDollarsTarget) {
      // No pledge to bill until both are known -- don't leave the last one standing.
      if (shared.hasAmountDollarsTarget) {
        shared.amountDollarsTarget.value = ""
        shared.updateAmountCents()
      }
      this.updateSidebar()
      this.updatePercentagePreviews()
      this.refreshMinimumPledge()
      return
    }

    // Written back as dollars (the shared controller's source of truth) but derived from the
    // rounded cents, so updateAmountCents round-trips to exactly monthlyCents.
    const monthlyCents = this.monthlyPledgeCents(incomeCents, percentage)
    shared.amountDollarsTarget.value = shared.isZeroDecimalCurrency ? String(monthlyCents) : (monthlyCents / multiplier).toFixed(2)
    shared.updateAmountCents()

    this.updateSidebar()
    this.updatePercentagePreviews()
    this.refreshMinimumPledge()
  }

  // Fixed monthly giving does not assert an income or a percentage-of-income commitment.
  applyCustomAmount() {
    const shared = this.sharedController
    const multiplier = shared.centsMultiplier
    const dollars = this.hasCustomAmountTarget ? parseFormattedNumber(this.customAmountTarget.value) : NaN
    const amountCents = isNaN(dollars) ? NaN : Math.round(dollars * multiplier)
    shared.amountDollarsTarget.value = isNaN(amountCents) ? "" : (shared.isZeroDecimalCurrency ? String(amountCents) : (amountCents / multiplier).toFixed(2))
    shared.updateAmountCents()

    this.updateSidebar()
    this.updatePercentagePreviews()
    this.refreshMinimumPledge()
  }

  // Each percentage option shows its own "$X/month" preview (not just the selected one) --
  // every button's own data-percentage against the entered income, independent of which is active.
  updatePercentagePreviews() {
    if (!this.hasPresetPercentageTarget) return

    const shared = this.sharedController
    const incomeDollars = this.incomeDollars
    const multiplier = shared?.centsMultiplier || 100

    this.presetPercentageTargets.forEach((btn) => {
      const preview = btn.querySelector('[data-one-for-the-world-donation-target="presetAmountPreview"]')
      if (!preview) return

      const percentage = parseFloat(btn.dataset.percentage)
      if (isNaN(incomeDollars) || isNaN(percentage)) {
        preview.textContent = "—"
        return
      }

      const monthlyCents = this.monthlyPledgeCents(Math.round(incomeDollars * multiplier), percentage)
      // Zero-width space: lets a narrow (mobile) card wrap "/month" onto its own line.
      preview.textContent = `${this.formatCopyCents(monthlyCents)}\u200B/month`
    })
  }

  // The one formula for a pledge's monthly amount -- mirrored exactly (same operands, same single
  // rounding) by Flows::Bricks::CapturePledgeCommitment.consistent_with_billed_amount?, which
  // re-derives it from the submitted estimated_annual_income_cents to check what was billed.
  monthlyPledgeCents(incomeCents, percentage) {
    return Math.round((incomeCents * percentage) / 1200)
  }
}
